/**
 * betCardV2Summary — pure summary builder for BetCardV2 (extracted so the
 * math-parity behavior is unit-testable under website/jest.config.js).
 *
 * PARITY CONTRACT (v1.52.x): every EV number this returns comes from
 * utils/canonicalEv.js — the same functions the results-list sort
 * (getCanonicalSortEv) and UnifiedBetCard (cleanPerBookEv) use. BetCardV2
 * must never read stored per-book `ev_percent` stamps except as a
 * last-resort fallback for legacy cache entries where canonicalEv cannot
 * resolve anything.
 */
import {
  cleanPerBookEv,
  getCanonicalSortEv,
  computeDevigMethodsSnapshot,
} from './canonicalEv';

export const extractPrice = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'object' && val.price !== undefined) return val.price;
  if (typeof val === 'number') return val;
  return null;
};

// Legacy stored per-book stamp — only used as a last-resort fallback when
// canonicalEv can't resolve (very old cache entries with no devig data).
export const extractStoredEv = (val) => {
  if (!val || typeof val !== 'object') return null;
  return val.ev_percent ?? val.ev_percent_clean ?? null;
};

export const canonBook = (name) => (name || '').replace(/\s*\([^)]*\)/g, '').trim();

/** Accepts hydrate `_deepLink` and raw `deep_link` / object {desktop,ios,android}. */
export function resolveDeepLink(odds) {
  if (odds == null) return null;
  if (typeof odds === 'string') return odds.length ? odds : null;
  if (typeof odds !== 'object') return null;
  const raw = odds._deepLink || odds.deep_link || odds.deeplink || odds.url || null;
  if (!raw) return null;
  if (typeof raw === 'string') return raw.length ? raw : null;
  if (typeof raw === 'object') return raw.desktop || raw.ios || raw.android || null;
  return null;
}

export function extractSummary(option, consensusOptions) {
  const raw = option?.raw || {};
  const isSGP = raw.ev_method === 'individual_leg_devig';

  const market = raw.market || option?.title || '';
  const selection = raw.selection || option?.description || option?.selection || '';
  const homeTeam = raw.home_team || raw.home_name || '';
  const awayTeam = raw.away_team || raw.away_name || '';
  const commenceTime = raw.commence_time || raw.start_date || raw.event_date || option?.date || null;
  const lineVal = option?.lineValue ?? raw.selection_line ?? raw.handicap ?? null;
  const sport = raw.sport || raw.league || '';

  // Build de-duped canonical book list
  const rawBookOdds =
    (option?.bookOdds && Object.keys(option.bookOdds).length > 0 ? option.bookOdds : null) ||
    (raw?.bookOdds && Object.keys(raw.bookOdds).length > 0 ? raw.bookOdds : null) ||
    (option?.raw?.bookOdds && Object.keys(option.raw.bookOdds).length > 0 ? option.raw.bookOdds : null) ||
    {};

  const _canonMap = new Map();
  for (const [book, odds] of Object.entries(rawBookOdds)) {
    if (odds === null || odds === undefined) continue;
    const canon = canonBook(book);
    const price = extractPrice(odds);
    // ⭐ PARITY: per-book EV comes from the SAME canonical function the results
    // sort and UnifiedBetCard use (cleanPerBookEv), not the stored per-book
    // ev_percent stamp (which diverges — it's composed differently for SGPs
    // and is stale/corrupt for ~60% of straight-bet cache entries).
    let evBook = cleanPerBookEv(raw, odds, isSGP, consensusOptions);
    if (evBook == null) evBook = extractStoredEv(odds); // legacy-cache fallback
    if (Number.isFinite(evBook)) evBook = parseFloat(evBook.toFixed(2));
    const existing = _canonMap.get(canon);
    if (!existing || (evBook !== null && (existing.ev === null || evBook > existing.ev))) {
      _canonMap.set(canon, { book: canon, odds, price, ev: evBook });
    }
  }

  // Sort by EV desc, then price desc
  const books = [..._canonMap.values()].sort((a, b) => {
    if (a.ev !== null && b.ev !== null) return b.ev - a.ev;
    if (a.ev !== null) return -1;
    if (b.ev !== null) return 1;
    if (a.price === null) return 1;
    if (b.price === null) return -1;
    return b.price - a.price;
  });

  const bookCount = books.length;
  const bestBook = books[0] || null;
  const bestPrice = bestBook?.price ?? null;
  const bestDeepLink = resolveDeepLink(bestBook?.odds) || resolveDeepLink(option) || null;

  // ⭐ PARITY: header pill EV = canonical sort EV (best cleanPerBookEv across
  // books) — the exact number the results list sorted this card by. Falls back
  // to the best per-book EV computed above, then the top-level stored stamp.
  let topEv = getCanonicalSortEv(option, null, consensusOptions);
  if (!Number.isFinite(topEv) || topEv === -999) {
    topEv = bestBook?.ev ?? raw.ev_percent_clean ?? raw.ev_percent ?? null;
  }
  if (Number.isFinite(topEv)) topEv = parseFloat(topEv.toFixed(2));
  else topEv = null;

  // Devig snapshot for the math section — same helper UnifiedBetCard's math
  // block uses, evaluated at the best price (matches the pill).
  let devigSnapshot = null;
  try {
    devigSnapshot = computeDevigMethodsSnapshot(option, [], bestPrice ?? undefined);
  } catch { devigSnapshot = null; }

  const fairProb =
    devigSnapshot?.power?.fairProb ??
    devigSnapshot?.multiplicative?.fairProb ??
    raw.consensus_fair_prob ?? raw.no_vig_prob ?? raw.fair_prob ?? null;
  const impliedProb = raw.implied_prob ??
    (bestPrice !== null && bestPrice !== undefined
      ? (bestPrice > 0 ? 100 / (bestPrice + 100) : Math.abs(bestPrice) / (Math.abs(bestPrice) + 100))
      : null);

  return {
    market, selection, homeTeam, awayTeam, commenceTime, lineVal, sport,
    books, bookCount, bestPrice, topEv, bestDeepLink,
    fairProb, impliedProb, devigSnapshot, isSGP,
  };
}
